import type { MouseEventHandler, ReactNode } from 'react';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useReportChartDrawn } from '../../../../shared/data-inspector/use-report-chart-drawn';
import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import { useGaChartPreviewMeta } from '../../../global-ai-assistant/ga-chart-preview-meta-context';
import { DataProviderQueryStatus } from '../DataProvider/types';
import { useTrackFullPagePlotError } from '../FullPageError/full-page-error.hooks';
import { ChartCardShell } from './ChartCardShell';
import { ChartError } from './ChartError';
import { ChartLoader } from './ChartLoader';
import { useChart, useSafeChart } from './ChartProvider';
import { useChartLoadingStatus } from './chart-menu/useChartLoadingStatus';
import { useClearChartSwitcherPlotWhenDone } from './hooks/useChartSwitcherPlot';

const ChartErrorBoundaryFallback = () => (
  <ChartError message="An error occurred while loading the chart. Please try refreshing the page." />
);

export const ChartFallback = ({
  status,
  zIndex = 0,
  children,
  errorMessage,
  onClick,
}: {
  status: DataProviderQueryStatus;
  zIndex?: number;
  children?: ReactNode;
  errorMessage?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { item } = useChart();
  const { onEditClick } = useSafeChart();
  const resolvedOnClick = onClick ?? onEditClick;
  const { kibanaLogger } = useKibanaLogger();
  const isChartLoading =
    status === DataProviderQueryStatus.Idle ||
    status === DataProviderQueryStatus.Fetching;
  const gaChartPreviewMeta = useGaChartPreviewMeta();

  useTrackFullPagePlotError(item?.id, status === DataProviderQueryStatus.Error);
  useClearChartSwitcherPlotWhenDone(!isChartLoading);
  useChartLoadingStatus(status);
  useReportChartDrawn(item?.id, isChartLoading);

  const handleChartError = useCallback(
    (error: Error) => {
      if (kibanaLogger === undefined) return;
      const message = error.stack
        ? `ChartErrorBoundary: ${error.message}\n${error.stack}`
        : `ChartErrorBoundary: ${error.message}`;
      console.error('ChartErrorBoundary', error);
      kibanaLogger.error(message, {
        context: 'ChartErrorBoundary',
      });
    },
    [kibanaLogger]
  );

  const renderGlobalAiPreviewChildren = (shell: ReactNode) => {
    if (!gaChartPreviewMeta) {
      return shell;
    }
    return (
      <>
        {children}
        {shell}
      </>
    );
  };

  if (status === DataProviderQueryStatus.Idle) {
    return renderGlobalAiPreviewChildren(
      <ChartCardShell onClick={resolvedOnClick}>
        <ChartLoader onClick={resolvedOnClick} />
      </ChartCardShell>
    );
  }

  if (status === DataProviderQueryStatus.Fetching) {
    return renderGlobalAiPreviewChildren(
      <ChartCardShell {...(zIndex && { zIndex })} onClick={resolvedOnClick}>
        <ChartLoader onClick={resolvedOnClick} />
      </ChartCardShell>
    );
  }

  if (status === DataProviderQueryStatus.Error) {
    return renderGlobalAiPreviewChildren(
      <ChartCardShell onClick={resolvedOnClick}>
        <ChartError message={errorMessage} />
      </ChartCardShell>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ChartErrorBoundaryFallback}
      onError={handleChartError}
    >
      {children}
    </ErrorBoundary>
  );
};
